import Header from '../../components/public/Header';
import Footer from '../../components/public/Footer';
import { Link } from 'react-router-dom';
import { MessageSquare, Phone, Mail, ArrowRight } from 'lucide-react';

const Section = ({ title, children }) => (
  <div>
    <h2 className="font-heading text-xl font-semibold text-text-primary mb-3">{title}</h2>
    <div className="text-text-muted leading-relaxed space-y-3">{children}</div>
    <hr className="border-[#E4E8EB] mt-6" />
  </div>
);

export default function ComplaintsPage() {
  return (
    <div className="min-h-screen bg-[#F8FAFA]">
      <Header />

      {/* Hero */}
      <section className="bg-white py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mx-auto mb-6">
            <MessageSquare className="h-8 w-8 text-primary" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-text-primary mb-4 leading-tight">
            Complaints &amp; Feedback
          </h1>
          <p className="text-lg text-text-muted leading-relaxed">
            We take all feedback seriously. If we have fallen short of your expectations, we want to know — and we will put it right.
          </p>
          <p className="text-sm text-text-muted mt-4">Last updated: June 2026</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-[#E4E8EB] p-8 space-y-6">

            <Section title="Our commitment">
              <p>
                Osabea Healthcare Solutions Limited is committed to providing high-quality, person-centred care and support. We recognise that occasionally things may not go as expected, and we believe that all complaints deserve a prompt, honest and compassionate response.
              </p>
              <p>
                This procedure is in place in accordance with <strong>CQC Regulation 16 (Receiving and Acting on Complaints)</strong> of the Health and Social Care Act 2008 (Regulated Activities) Regulations 2014. We treat every complaint as an opportunity to learn and improve.
              </p>
            </Section>

            <Section title="What you can complain about">
              <p>You may make a complaint about any aspect of our care and support services, including:</p>
              <ul className="list-disc list-inside space-y-1 mt-1">
                <li>The standard or quality of care provided</li>
                <li>Conduct or behaviour of any member of our staff</li>
                <li>Delays or failures in service delivery</li>
                <li>Communication failures or lack of information</li>
                <li>How your personal information has been handled</li>
                <li>Any other aspect of your experience with Osabea Healthcare Solutions</li>
              </ul>
            </Section>

            <Section title="How to make a complaint">
              <p>You can raise a complaint in any of the following ways:</p>
              <ul className="list-disc list-inside space-y-1 mt-1">
                <li><strong>By telephone:</strong> Call us on <a href="tel:01622809469" className="text-primary underline">01622 809469</a></li>
                <li><strong>By email:</strong> <a href="mailto:info@osabeacares.co.uk" className="text-primary underline">info@osabeacares.co.uk</a></li>
                <li><strong>In writing:</strong> Osabea Healthcare Solutions Limited, Suite FA4D, 1 St Faith's Street, Maidstone, Kent, ME14 1LH</li>
                <li><strong>In person:</strong> Contact our office to arrange a meeting at a convenient time</li>
              </ul>
              <p>
                You may also ask a family member, advocate, or representative to make a complaint on your behalf. If you need a translation or an accessible format of this procedure, please let us know and we will arrange this.
              </p>
            </Section>

            <Section title="Our complaints process and timescales">
              <p>Once we receive your complaint, we will follow these steps:</p>
              <ol className="list-decimal list-inside space-y-2 mt-2">
                <li>
                  <strong>Acknowledgement:</strong> We will acknowledge receipt of your complaint within <strong>3 working days</strong>, confirming who is handling it and the expected timescale.
                </li>
                <li>
                  <strong>Investigation:</strong> We will conduct a thorough, impartial investigation of the issues raised. This may involve speaking with staff, reviewing records, and gathering relevant information.
                </li>
                <li>
                  <strong>Response:</strong> We will provide a full written response within <strong>20 working days</strong> of receiving the complaint, unless the matter is particularly complex (in which case we will keep you informed of progress and revised timescales).
                </li>
                <li>
                  <strong>Outcome:</strong> Our response will explain our findings, any actions we have taken or will take, any apology where appropriate, and your right to escalate if you remain dissatisfied.
                </li>
              </ol>
            </Section>

            <Section title="If you are not satisfied with our response">
              <p>If you have gone through our complaints process and remain dissatisfied, you have the right to escalate your complaint to an independent body:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>
                  <strong>Care Quality Commission (CQC):</strong> The CQC regulates health and social care services. You can report a concern at{' '}
                  <a href="https://www.cqc.org.uk/give-feedback-on-care" target="_blank" rel="noopener noreferrer" className="text-primary underline">cqc.org.uk/give-feedback-on-care</a> or call 03000 616161.
                </li>
                <li>
                  <strong>Local Government and Social Care Ombudsman:</strong> If you are a social care client and remain unhappy after our formal response, you may refer your complaint to the Local Government and Social Care Ombudsman at{' '}
                  <a href="https://www.lgo.org.uk" target="_blank" rel="noopener noreferrer" className="text-primary underline">lgo.org.uk</a> or call 0300 061 0614.
                </li>
                <li>
                  <strong>Information Commissioner's Office (ICO):</strong> If your complaint relates to how we handle personal data, you may contact the ICO at{' '}
                  <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-primary underline">ico.org.uk</a> or call 0303 123 1113.
                </li>
              </ul>
            </Section>

            <Section title="Our record keeping">
              <p>
                We maintain a confidential complaints register that records all complaints received, the outcome, and any actions taken. This register is reviewed regularly by our management team and is available for inspection by the CQC.
              </p>
              <p>
                Making a complaint will never result in any detriment to you, your care, or your employment with us.
              </p>
            </Section>

          </div>

          {/* Contact CTA */}
          <div className="bg-white rounded-2xl border border-[#E4E8EB] p-8 mt-6">
            <h2 className="font-heading text-xl font-semibold text-text-primary mb-4">Contact us now</h2>
            <div className="space-y-3">
              <a href="tel:01622809469" className="flex items-center gap-4 rounded-xl border border-[#E4E8EB] p-4 bg-[#F8FAFA] hover:border-primary/40 transition-colors">
                <Phone className="h-5 w-5 text-primary flex-shrink-0" />
                <div><div className="text-xs text-text-muted">Telephone</div><div className="font-medium">01622 809469</div></div>
              </a>
              <a href="mailto:info@osabeacares.co.uk" className="flex items-center gap-4 rounded-xl border border-[#E4E8EB] p-4 bg-[#F8FAFA] hover:border-primary/40 transition-colors">
                <Mail className="h-5 w-5 text-primary flex-shrink-0" />
                <div><div className="text-xs text-text-muted">Email</div><div className="font-medium">info@osabeacares.co.uk</div></div>
              </a>
            </div>
            <div className="mt-6 pt-6 border-t border-[#E4E8EB]">
              <p className="text-sm text-text-muted mb-3">You can also report a concern directly to the CQC:</p>
              <a href="https://www.cqc.org.uk/give-feedback-on-care" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-primary font-medium hover:underline">
                Give feedback on care at CQC <ArrowRight className="h-3 w-3" />
              </a>
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}
